import { JobPostingTabData } from '../../../../../../../utils/types/JobOpenings';

/**
 * Checks if the Job Post Settings step is completed
 */
export const isSettingsStepCompleted = (
  jobPostingData?: JobPostingTabData,
): boolean => {
  if (!jobPostingData) return false;
  return Boolean(
    jobPostingData.jobTitle &&
      jobPostingData.location &&
      jobPostingData.department &&
      jobPostingData.workType,
  );
};

/**
 * Checks if the Video Message step is completed
 */
export const isVideoStepCompleted = (
  jobPostingData?: JobPostingTabData,
): boolean => {
  if (!jobPostingData) return false;
  return Boolean(jobPostingData.enableVideo && jobPostingData.videoFilename);
};

/**
 * Checks if the Pre-Qualifying Questions step is completed
 */
export const isPrequalStepCompleted = (
  preQualQuestionsCount: number,
): boolean => {
  return preQualQuestionsCount > 0;
};

/**
 * Checks if the CCA step is completed
 */
export const isCcaStepCompleted = (
  ccaEnabled: boolean,
  ccaQuestionsCount: number,
): boolean => {
  return Boolean(ccaEnabled && ccaQuestionsCount > 0);
};

/**
 * Generates descriptive summary text for the Settings step
 */
export const getSettingsSummary = (
  jobPostingData?: JobPostingTabData,
): string => {
  if (!jobPostingData || !jobPostingData.jobTitle) {
    return 'Configure dates, job title, location, working arrangement, etc.';
  }

  const parts: string[] = [jobPostingData.jobTitle];
  if (jobPostingData.location) parts.push(jobPostingData.location);
  if (jobPostingData.workType) parts.push(jobPostingData.workType);
  if (jobPostingData.department) parts.push(`Dept: ${jobPostingData.department}`);

  return parts.join(' • ');
};

/**
 * Generates descriptive summary text for the Video Message step
 */
export const getVideoSummary = (
  jobPostingData?: JobPostingTabData,
): string => {
  if (!jobPostingData || !jobPostingData.enableVideo) {
    return 'Video message greeting is disabled for this job post.';
  }
  if (jobPostingData.videoTitle) {
    return `Video Message Enabled • "${jobPostingData.videoTitle}"`;
  }
  return 'Video Message Enabled';
};

/**
 * Generates descriptive summary text for Pre-Qualifying Questions step
 */
export const getPrequalSummary = (preQualQuestionsCount: number): string => {
  if (preQualQuestionsCount > 0) {
    return `${preQualQuestionsCount} screening question(s) configured.`;
  }
  return 'Define qualifying/screening questions for applicants.';
};

/**
 * Generates descriptive summary text for CCA step
 */
export const getCcaSummary = (
  ccaEnabled: boolean,
  ccaQuestionsCount: number,
): string => {
  if (ccaEnabled) {
    return `CCA Enabled • ${ccaQuestionsCount} assessment question(s) selected.`;
  }
  return 'CCA is disabled for this job post.';
};

/**
 * Calculates progress percentage between 0 and 100
 */
export const calculateProgress = (
  completedCount: number,
  totalCount: number,
): number => {
  if (totalCount <= 0) return 0;
  return Math.min(100, Math.round((completedCount / totalCount) * 100));
};
